import { createSlice } from "@reduxjs/toolkit";
import {
  fetchProductEnquiries,
  fetchProductEnquiryById,
  createProductEnquiry,
  updateProductEnquiry,
  deleteProductEnquiry,
  toggleProductEnquiryStatus,
  fetchProductEnquiryDropdown,
  fetchProductEnquiryDynamicDropdown,
  fetchServiceEnquiries,
  fetchServiceEnquiryById,
  createServiceEnquiry,
  updateServiceEnquiry,
  deleteServiceEnquiry,
  toggleServiceEnquiryStatus,
  fetchServiceEnquiryDropdown,
  fetchServiceEnquiryDynamicDropdown,
  fetchProductEnquiryOrderDropdown,
  fetchServiceEnquiryOrderDropdown,
  fetchCategories,
  fetchSubcategories,
  fetchMakes,
  fetchModels,
  createEnquiryFollowUp,
  handleProductEnquiryStatus,
  handleServiceEnquiryStatus
} from "./thunk";

interface EnquiryState {
  productList: any[];
  serviceList: any[];
  rowCount: number;
  loading: boolean;
  error: string | null;
  selectedEnquiry: any;
  dropdown: any[];
  orderDropdown: any[];
  dynamicDropdown: any[];
  categories: any[];
  subCategories: any[];
  makes: any[];
  models: any[];
  followUps: any[];
  stage: any[]
  todaysFollowUpCount?: number;
};

const initialState: EnquiryState = {
  productList: [],
  serviceList: [],
  rowCount: 0,
  loading: false,
  error: null,
  selectedEnquiry: null,
  dropdown: [],
  orderDropdown: [],
  dynamicDropdown: [],
  categories: [],
  subCategories: [],
  makes: [],
  models: [],
  followUps: [],
  stage: [],
  todaysFollowUpCount: 0,
};

const enquirySlice = createSlice({
  name: "enquiry",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // ----------------------------------------------------
    // ✅ PRODUCT ENQUIRIES
    // ----------------------------------------------------
    builder
      .addCase(fetchProductEnquiries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductEnquiries.fulfilled, (state, action) => {
        state.loading = false;
        state.productList = action?.payload.data;
        state.rowCount = action.payload.rowCount;
        state.todaysFollowUpCount = action.payload.todaysFollowUpCount;
      })
      .addCase(fetchProductEnquiries.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch product enquiries";
      })

      .addCase(fetchProductEnquiryById.fulfilled, (state, action) => {
        console.log("action payload : ", action)
        state.loading = false;
        state.selectedEnquiry = action.payload;
      })
      .addCase(fetchProductEnquiryById.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.error = typeof payload === "string" ? payload : (payload?.message || payload?.error || "Failed to fetch Product Enquiry by ID");
      })

      .addCase(createProductEnquiry.fulfilled, (state, action) => {
        state.productList.unshift(action.payload);
      })
      .addCase(updateProductEnquiry.fulfilled, (state, action) => {
        if (!action.payload?.id) return;
        state.productList = state.productList.map((enquiry) =>
          enquiry.id === action.payload.id ? action.payload : enquiry
        );
      })
      .addCase(deleteProductEnquiry.fulfilled, (state, action) => {
        state.productList = state.productList.filter(
          (enquiry) => enquiry.id !== action.payload
        );
      })
      .addCase(toggleProductEnquiryStatus.fulfilled, (state, action) => {
        if (!action.payload?.id) return;
        state.productList = state.productList.map((enquiry) =>
          enquiry.id === action.payload.id ? action.payload : enquiry
        );
      })
      .addCase(fetchProductEnquiryDropdown.fulfilled, (state, action) => {
        state.dropdown = action.payload?.data || [];
      })
      .addCase(
        fetchProductEnquiryDynamicDropdown.fulfilled,
        (state, action) => {
          state.dynamicDropdown = action.payload?.data || [];
        }
      );

    // Followups

    builder
      .addCase(createEnquiryFollowUp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEnquiryFollowUp.fulfilled, (state, action) => {
        console.log("action in enquiry follow up : ", action)
        state.loading = false;
        state.followUps = action.payload.data;
        state.rowCount = action.payload.rowCount;
      })
      .addCase(createEnquiryFollowUp.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to create followUps";
      })



    // ----------------------------------------------------
    // ✅ SERVICE ENQUIRIES
    // ----------------------------------------------------
    builder
      .addCase(fetchServiceEnquiries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServiceEnquiries.fulfilled, (state, action) => {
        state.loading = false;
        state.serviceList = action.payload.data;
        state.rowCount = action.payload.rowCount;
        state.todaysFollowUpCount = action.payload.todaysFollowUpCount;
      })
      .addCase(fetchServiceEnquiries.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch service enquiries";
      })

      .addCase(fetchServiceEnquiryById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedEnquiry = action.payload;
      })
      .addCase(fetchServiceEnquiryById.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.error = typeof payload === "string" ? payload : (payload?.message || payload?.error || "Failed to fetch Service Enquiry by ID");
      })

      .addCase(createServiceEnquiry.fulfilled, (state, action) => {
        state.serviceList.unshift(action.payload);
      })
      .addCase(updateServiceEnquiry.fulfilled, (state, action) => {
        if (!action.payload?.id) return;
        state.serviceList = state.serviceList.map((enquiry) =>
          enquiry.id === action.payload.id ? action.payload : enquiry
        );
      })
      .addCase(deleteServiceEnquiry.fulfilled, (state, action) => {
        state.serviceList = state.serviceList.filter(
          (enquiry) => enquiry.id !== action.payload
        );
      })
      .addCase(toggleServiceEnquiryStatus.fulfilled, (state, action) => {
        if (!action.payload?.id) return;
        state.serviceList = state.serviceList.map((enquiry) =>
          enquiry.id === action.payload.id ? action.payload : enquiry
        );
      })
      .addCase(fetchServiceEnquiryDropdown.fulfilled, (state, action) => {
        state.dropdown = action.payload?.data || [];
      })
      .addCase(
        fetchServiceEnquiryDynamicDropdown.fulfilled,
        (state, action) => {
          state.dynamicDropdown = action.payload?.data || [];
        }
      )
      .addCase(fetchProductEnquiryOrderDropdown.fulfilled, (state, action) => {
        state.orderDropdown = action.payload?.data || [];
      })
      .addCase(fetchServiceEnquiryOrderDropdown.fulfilled, (state, action) => {
        state.orderDropdown = action.payload?.data || [];
      })
      .addCase(fetchCategories.pending, (state) => {

        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;

        state.categories = action.payload.data || action.payload || [];
        // Clear dependent dropdowns
        state.subCategories = [];
        state.makes = [];
        state.models = [];
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.error = typeof payload === "string" ? payload : (payload?.message || payload?.error || "Failed to fetch categories");
      })

      .addCase(fetchSubcategories.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchSubcategories.fulfilled, (state, action) => {
        // Assuming the payload is an array of subcategories
        state.subCategories = action.payload.data || action.payload || [];
        // Clear dependent dropdowns
        state.makes = [];
        state.models = [];
      })
      .addCase(fetchSubcategories.rejected, (state, action) => {
        const payload = action.payload as any;
        state.error = typeof payload === "string" ? payload : (payload?.message || payload?.error || "Failed to fetch subcategories");
      })

      .addCase(fetchMakes.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchMakes.fulfilled, (state, action) => {
        // Assuming the payload is an array of makes
        state.makes = action.payload.data || action.payload || [];
        // Clear dependent dropdown
        state.models = [];
      })
      .addCase(fetchMakes.rejected, (state, action) => {
        const payload = action.payload as any;
        state.error = typeof payload === "string" ? payload : (payload?.message || payload?.error || "Failed to fetch makes");
      })

      .addCase(fetchModels.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchModels.fulfilled, (state, action) => {
        // Assuming the payload is an array of models
        state.models = action.payload.data || action.payload || [];
      })
      .addCase(fetchModels.rejected, (state, action) => {
        const payload = action.payload as any;
        state.error = typeof payload === "string" ? payload : (payload?.message || payload?.error || "Failed to fetch models");
      })
      .addCase(handleProductEnquiryStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(handleProductEnquiryStatus.fulfilled, (state, action) => {
        // Assuming the payload is an array of models
        state.stage = action.payload.data || action.payload || [];
      })
      .addCase(handleProductEnquiryStatus.rejected, (state, action) => {
        const payload = action.payload as any;
        state.error = typeof payload === "string" ? payload : (payload?.message || payload?.error || "Failed to update product enquiry status");
      })
      .addCase(handleServiceEnquiryStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(handleServiceEnquiryStatus.fulfilled, (state, action) => {
        // Assuming the payload is an array of models
        state.stage = action.payload.data || action.payload || [];
      })
      .addCase(handleServiceEnquiryStatus.rejected, (state, action) => {
        const payload = action.payload as any;
        state.error = typeof payload === "string" ? payload : (payload?.message || payload?.error || "Failed to update service enquiry status");
      });
  },
});

export default enquirySlice.reducer;
